import { useQuery } from '@tanstack/react-query';
import { Client, ClientListItem } from 'src/admin/users/types';
import { api } from 'src/common/utils/api';

type ClientsFilters = {
  advisorUserId?: string;
  familyId?: string;
  isTest?: boolean;
  companyId?: string;
};

type ClientsQueryParams = {
  advisorUserId?: string;
  familyId?: string;
  isTest?: boolean;
};

export const CLIENTS_PAGE_SIZE = 1000;

const PAGE_SORT = { sortBy: 'name', sortDir: 'asc' } as const;

const toQueryParams = (filters: ClientsFilters): ClientsQueryParams => {
  const params: ClientsQueryParams = {};
  if (filters.advisorUserId) params.advisorUserId = filters.advisorUserId;
  if (filters.familyId) params.familyId = filters.familyId;
  if (filters.isTest !== undefined) params.isTest = filters.isTest;
  return params;
};

const fetchClientsPage = (params: ClientsQueryParams, page: number) =>
  api.getPaginated<ClientListItem>('clients', {
    ...params,
    ...PAGE_SORT,
    page,
    size: CLIENTS_PAGE_SIZE,
  });

const fetchAllClientPages = async (params: ClientsQueryParams): Promise<ClientListItem[]> => {
  const firstPage = await fetchClientsPage(params, 1);
  if (firstPage.items.length >= firstPage.total) return firstPage.items;

  const pageCount = Math.ceil(firstPage.total / CLIENTS_PAGE_SIZE);

  const remainingPages = await Promise.all(
    Array.from({ length: pageCount - 1 }, (_, index) => fetchClientsPage(params, index + 2)),
  );
  return [firstPage, ...remainingPages].flatMap(page => page.items);
};

// The admin per-company endpoint is not paginated, so it is fetched in one request.
const fetchCompanyClients = async (companyId: string, params: ClientsQueryParams) => {
  const { data } = await api.get<Client[]>(`admin/companies/${companyId}/clients`, params);
  return data;
};

export const useClients = (filters: ClientsFilters = {}, options: { enabled?: boolean } = {}) => {
  const params = toQueryParams(filters);
  const { companyId } = filters;

  const { data, isLoading, isError, refetch } = useQuery<ClientListItem[]>({
    queryKey: companyId
      ? ['admin', 'companies', companyId, 'clients', params]
      : ['clients', 'all-pages', params],
    queryFn: () =>
      companyId ? fetchCompanyClients(companyId, params) : fetchAllClientPages(params),
    enabled: options.enabled,
  });

  return {
    clients: data ?? [],
    isLoading,
    isError,
    refetch,
  };
};
